import api from "core/api/axiosInstance";
import {
  isValidPlate,
  normalizePlate,
  normalizePlateResult,
  plateCommerceId,
} from "../placas/model/plateModel";
const auth = () => {
  if (!localStorage.getItem("token"))
    throw new Error("Su sesión ha expirado. Inicie sesión nuevamente.");
};
const explicitlyFalse = (value) => [false, "false", 0, "0"].includes(value);
const failed = (error, httpStatus = null, extra = {}) => ({
  success: false,
  error,
  message: error,
  httpStatus,
  canRetry: ![401, 403].includes(httpStatus),
  ...extra,
});
const getMessage = (data) => {
  const value =
    data?.message ?? data?.error ?? data?.detail ?? data?.response?.message ?? data?.msm;
  return typeof value === "string" ? value : null;
};
const isNotGopass = (data) =>
  data?.isNotGopass === true ||
  (data?.ok === false &&
    String(data.message || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .includes("vehiculo no es gopass"));
export async function getCommerceListService() {
  try {
    auth();
    const { data } = await api.get("/api/automatization/plate/get-list-commerce-hdwr");
    const list = Array.isArray(data) ? data : data?.response ?? data?.data;
    if (!Array.isArray(list) || explicitlyFalse(data?.ok) || explicitlyFalse(data?.success))
      throw new Error(getMessage(data) || "La respuesta de comercios no contiene una lista válida");
    const normalized = list.map((item) => ({
      ...item,
      idcommerce: plateCommerceId(item?.idcommerce ?? item?.id),
      namecomercio: String(item?.namecomercio ?? item?.commerce ?? item?.name ?? "").trim(),
    }));
    if (normalized.some((item) => item.idcommerce === null || !item.namecomercio))
      throw new Error("La lista contiene comercios sin nombre o ID válido");
    return { success: true, data: normalized };
  } catch (error) {
    return failed(
      getMessage(error.response?.data) ||
        error.message ||
        "No se pudo cargar la lista de comercios",
      error.response?.status
    );
  }
}
export async function getPlateStatusService(commerceId, plate, { signal } = {}) {
  try {
    auth();
    const id = plateCommerceId(commerceId);
    if (id === null) throw new Error("Seleccione un comercio válido");
    if (!isValidPlate(plate)) throw new Error("Formato de placa inválido. Use ABC123 o ABC12A");
    const formatted = normalizePlate(plate);
    const { data, status } = await api.get(
      `/api/automatization/plate/get-list-status-hdwr/${id}/${encodeURIComponent(formatted)}`,
      { timeout: 120000, ...(signal ? { signal } : {}) }
    );
    const info = data?.response ?? data?.data ?? data;
    if (info?.plate && normalizePlate(info.plate) !== formatted)
      return failed("La respuesta corresponde a otra placa. Reintente la consulta.", status);
    if (isNotGopass(info))
      return {
        success: false,
        isNotGopass: true,
        canRetry: false,
        data: normalizePlateResult({ ...info, isNotGopass: true }, formatted),
        message: info.message || "Vehículo no es Gopass",
        httpStatus: status,
      };
    const valid =
      info &&
      !Array.isArray(info) &&
      typeof info === "object" &&
      ([1, 2, 3, 4, "1", "2", "3", "4"].includes(info.status) ||
        [
          "status_description",
          "status_vehicle_poseidon",
          "status_user_poseidon",
          "vehicle_list_commerce_poseidon",
        ].some((key) => info[key] !== null && info[key] !== undefined && info[key] !== ""));
    if (!valid)
      return failed(
        getMessage(info) ||
          getMessage(data) ||
          "El servicio no devolvió información de estado válida",
        status
      );
    const normalized = normalizePlateResult(info, formatted);
    if ([info.ok, info.success, data?.success, data?.ok].some(explicitlyFalse))
      return failed(
        getMessage(info) || info.status_description || "La consulta fue rechazada por el servicio",
        status,
        { data: normalized, canRetry: info.canRetry !== false }
      );
    return {
      success: true,
      data: normalized,
      httpStatus: status,
      message: "Consulta recibida correctamente",
      canRetry: false,
    };
  } catch (error) {
    if (error.code === "ERR_CANCELED" || signal?.aborted) return { success: false, canceled: true };
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT")
      return failed(
        "Se agotó el tiempo de espera. No se pudo confirmar el resultado; puede reintentar la consulta.",
        408,
        { isTimeout: true }
      );
    const status = error.response?.status;
    const info = error.response?.data?.response ?? error.response?.data;
    if (isNotGopass(info))
      return {
        success: false,
        isNotGopass: true,
        canRetry: false,
        data: normalizePlateResult({ ...info, isNotGopass: true }, plate),
        message: info.message || "Vehículo no es Gopass",
        httpStatus: status,
      };
    const fallback =
      status === 401
        ? "Su sesión ha expirado. Inicie sesión nuevamente."
        : status === 403
        ? "No tiene permiso para realizar esta consulta"
        : status === 404
        ? "No se encontró información para este comercio y placa"
        : error.request
        ? "No se pudo conectar con el servidor"
        : error.message;
    return failed(getMessage(info) || fallback || "No se pudo consultar la placa", status);
  }
}
export default { getCommerceList: getCommerceListService, getPlateStatus: getPlateStatusService };
